import { useState } from "react";
import { AxiosResponse } from "axios";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { DeleteRequest } from "shared/api/api";
import { mutateMultipleSWRRequestKeys } from "shared/utils";

import IconButton from "features/components/IconButton";
import DeleteConfirmationModal from "features/ui/DeleteConfirmationModal";
import DeleteIcon from "features/ui/Icons/DeleteIcon";
import LinkButton from "features/ui/LinkButton";
import { toast } from "features/ui/Toast";

export interface RequiredProps {
  ID: string;
  name: string;
}

interface Props<T> {
  data: T;
  entityName: string;
  deleteCallback: (args: DeleteRequest) => Promise<AxiosResponse<any, any>>;
  onSuccessNavigateToRoute?: string;
  onSuccessDeleteText?: string;
  showButton?: boolean;
  defaultOpen?: boolean;
  onClose?: () => void;
  iconOnly?: boolean;
  entityRequestKeys?: string[];
  disabled?: boolean;
}

const DeleteAction = <T extends RequiredProps>({
  data: { ID, name },
  entityName,
  deleteCallback,
  onSuccessNavigateToRoute,
  onSuccessDeleteText,
  showButton = true,
  defaultOpen = false,
  iconOnly = false,
  onClose,
  entityRequestKeys,
  disabled,
}: Props<T>) => {
  const { t } = useTranslation("common");
  const errorText = t("delete.error");
  const successText = onSuccessDeleteText ?? t("delete.success");

  const navigate = useNavigate();

  const [modalVisible, setModalVisible] = useState(defaultOpen);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmationError, setConfirmationError] = useState<string>();

  const openModal = () => {
    setConfirmationError(undefined);
    setModalVisible(true);
  };

  const handleOnConfirm = (confirm: boolean) => {
    if (!confirm) {
      setModalVisible(false);
      onClose && onClose();

      return;
    }

    setIsDeleting(true);
    deleteCallback({ id: ID })
      .then(() => {
        toast.success(successText);
        if (entityRequestKeys) {
          mutateMultipleSWRRequestKeys(entityRequestKeys);
        }

        setModalVisible(false);
        onClose && onClose();

        if (onSuccessNavigateToRoute) {
          navigate(onSuccessNavigateToRoute);
        }
      })
      .catch((err) => {
        setConfirmationError(
          err.response?.status === 409 ? t("delete.conflict") : errorText
        );
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  const deleteButton = iconOnly ? (
    <IconButton
      onClick={(e) => {
        e.stopPropagation();
        openModal();
      }}
      data-testid="btn-delete"
      size="small"
      disabled={disabled}
      aria-label={t("delete.action")}
    >
      <DeleteIcon />
    </IconButton>
  ) : (
    <LinkButton
      onClick={openModal}
      data-testid="btn-delete"
      className={classNames("no-underline", {
        "text-gray-400 cursor-not-allowed opacity-60 pointer-events-none":
          disabled,
        "text-red-600 hover:underline cursor-pointer": !disabled,
      })}
    >
      {t("delete.action")}
    </LinkButton>
  );

  return (
    <>
      {showButton && deleteButton}
      <DeleteConfirmationModal
        isOpen={modalVisible}
        onClose={handleOnConfirm}
        entityName={entityName}
        name={name}
        loading={isDeleting}
        error={confirmationError}
      />
    </>
  );
};
export default DeleteAction;
